import moment from 'moment';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';
import getSymbolFromCurrency from 'currency-symbol-map';
import { getVendorMappingsStore } from 'app/store/vendorsStore/vendorStoreProvider';
import InfoPopover from '~/shared/components/andtComponents/InfoPopover/index.jsx';
import { CLOUD_TYPE_IDS } from '~/users/constants/usersConstants.js';
import React from 'react';

const ColumnsNames = {
  Onboarding: 'onboarding',
  CloudTypeId: 'cloudTypeId',
  AccountName: 'accountName',
  AccountId: 'accountId',
  LastProcessTime: 'lastProcessTime',
  AccountLastProcessTime: 'accountProcessTime',
  DbCreationDate: 'dbCreationTime',
  Details: 'details',
  ProviderCurrency: 'providerCurrency',
  ProcessingCurrency: 'processingCurrency',
  IsDefault: 'isDefault',
  Edit: 'edit',
  Delete: 'delete',
  FilterOutMicrosoft365: 'isFilterOutMicrosoft365',
};

const Properties = {
  Key: 'name',
  CloudTypeIds: 'cloudTypeIds',
  Title: 'title',
  Direction: 'direction',
  Width: 'width',
  FlexGrow: 'flexGrow',
  GetCellValue: 'getCellValue',
  SortingEnabled: 'sortingEnabled',
  ColumnName: 'columnName',
  Compare: 'compare',
  Tooltip: 'tooltip',
  TooltipPlacement: 'tooltipPlacement',
};

const dateCompare = (a, b) => {
  if (!a) return -1;
  if (!b) return 1;

  const dateA = new Date(a);
  const dateB = new Date(b);
  return dateA - dateB;
};

const columnsListA = [
  {
    [Properties.Key]: ColumnsNames.Onboarding,
    [Properties.ColumnName]: ColumnsNames.Onboarding,
    [Properties.Title]: ColumnsNames.Onboarding,
    [Properties.Direction]: 'desc',
    [Properties.SortingEnabled]: true,
  },
  {
    [Properties.Key]: ColumnsNames.CloudTypeId,
    [Properties.ColumnName]: ColumnsNames.CloudTypeId,
    [Properties.Title]: 'Cloud Type',
    [Properties.Width]: 120,
    [Properties.Direction]: 'asc',
    [Properties.SortingEnabled]: true,
    [Properties.GetCellValue]: (row) => getVendorMappingsStore().getDescriptionById(row.cloudTypeId),
  },
  {
    [Properties.Key]: ColumnsNames.AccountName,
    [Properties.ColumnName]: ColumnsNames.AccountName,
    [Properties.Title]: 'Account Name',
    [Properties.FlexGrow]: 1,
    [Properties.Width]: 220,
    [Properties.SortingEnabled]: true,
  },
  {
    [Properties.Key]: ColumnsNames.AccountId,
    [Properties.ColumnName]: ColumnsNames.AccountId,
    [Properties.Title]: 'Account ID',
    [Properties.Direction]: 'asc',
    [Properties.FlexGrow]: 1,
    [Properties.Width]: 190,
    [Properties.SortingEnabled]: true,
  },
  {
    [Properties.Key]: ColumnsNames.LastProcessTime,
    [Properties.ColumnName]: ColumnsNames.LastProcessTime,
    [Properties.Width]: 160,
    [Properties.SortingEnabled]: true,
    [Properties.Compare]: dateCompare,
    [Properties.Title]: 'Last Date in File', // The DB column name is misleading, so we're fixing it here. See https://anodot.atlassian.net/browse/PIL-10984
    [Properties.Tooltip]: 'The most recent date included in the last processed file.',
    [Properties.TooltipPlacement]: 'top',
  },
  {
    [Properties.Key]: ColumnsNames.AccountLastProcessTime,
    [Properties.ColumnName]: ColumnsNames.AccountLastProcessTime,
    [Properties.Width]: 160,
    [Properties.SortingEnabled]: true,
    [Properties.Compare]: dateCompare,
    [Properties.Title]: 'Last Processed Time', // The DB column name is misleading, so we're fixing it here. See https://anodot.atlassian.net/browse/PIL-10984
    [Properties.Tooltip]: 'The processing time of the last file processed in this account.',
    [Properties.TooltipPlacement]: 'top',
    [Properties.GetCellValue]: (row) =>
      row.accountProcessTime ? moment(row.accountProcessTime).format('MMMM DD YYYY') : '',
  },
  {
    [Properties.Key]: ColumnsNames.DbCreationDate,
    [Properties.ColumnName]: ColumnsNames.DbCreationDate,
    [Properties.Width]: 160,
    [Properties.Title]: 'Creation Date',
    [Properties.SortingEnabled]: true,
    [Properties.Compare]: dateCompare,
    [Properties.GetCellValue]: (row) => (row.dbCreationTime ? moment(row.dbCreationTime).format('MMMM DD YYYY') : ''),
  },
  {
    [Properties.Key]: ColumnsNames.Details,
    [Properties.ColumnName]: ColumnsNames.Details,
    [Properties.Title]: 'Details',
    [Properties.Width]: 200,
    [Properties.FlexGrow]: 1,
    [Properties.SortingEnabled]: true,
    [Properties.Compare]: (aDetailsAsString, bDetailsAsString) => {
      if (isEmpty(aDetailsAsString) && isEmpty(bDetailsAsString)) {
        return 0;
      }
      if (isEmpty(aDetailsAsString)) {
        return 1;
      }
      if (isEmpty(bDetailsAsString)) {
        return -1;
      }
      if (bDetailsAsString.length === aDetailsAsString.length) {
        return bDetailsAsString - aDetailsAsString;
      }
      return bDetailsAsString.length - aDetailsAsString.length;
    },
    [Properties.GetCellValue]: (row) => {
      return !isEmpty(row.accountInfo)
        ? `${Object.keys(row.accountInfo)
            .map((key) => {
              return `${key}: ${row.accountInfo?.[key]}`;
            })
            .join(';\r')}`
        : '';
    },
  },
  {
    [Properties.Key]: ColumnsNames.ProviderCurrency,
    [Properties.ColumnName]: ColumnsNames.ProviderCurrency,
    [Properties.Title]: 'Provider Currency',
    [Properties.Width]: 120,
    [Properties.FlexGrow]: 1,
    [Properties.Tooltip]: `The billing currency as configured in the provider's console.`,
    [Properties.TooltipPlacement]: 'top',
    [Properties.SortingEnabled]: true,
    [Properties.GetCellValue]: (row) => {
      if (row.onboarding) {
        return null;
      }
      return `${row.providerCurrency} (${getSymbolFromCurrency(row.providerCurrency)})`;
    },
  },
];

const columnsListB = [
  {
    [Properties.Key]: ColumnsNames.Delete,
    [Properties.ColumnName]: ColumnsNames.Delete,
    [Properties.Width]: 70,
    [Properties.SortingEnabled]: false,
    [Properties.Title]: 'Delete',
  },
];

const buildColumnsConfig = (isCurrencyFeatureFlagActive, isMSPWithAzureAccounts, brandName = 'Umbrella') => {
  const copyColumnsListB = [...columnsListB];
  if (isMSPWithAzureAccounts) {
    copyColumnsListB.splice(copyColumnsListB.length - 1, 0, {
      [Properties.Key]: ColumnsNames.FilterOutMicrosoft365,
      [Properties.ColumnName]: ColumnsNames.FilterOutMicrosoft365,
      [Properties.Width]: 110,
      [Properties.CloudTypeIds]: [CLOUD_TYPE_IDS.AZURE],
      [Properties.Title]: (
        <>
          Exclude Micro...
          <span style={{ pointerEvents: 'all' }}>
            <InfoPopover isSimple placement="top">
              <p key={ColumnsNames.FilterOutMicrosoft365}>
                Exclude the records with following <strong>ServiceFamily</strong> values from your billing data so they
                won&#39;t appear in the platform:
              </p>
              <ul key="list">
                <li key="Office">Office 365 Global</li>
                <li key="CRM">CRM</li>
                <li key="Commercial">Windows Commercial</li>
                <li key="Intune">Windows Intune</li>
              </ul>
            </InfoPopover>
          </span>
        </>
      ),
    });
  }
  if (!isCurrencyFeatureFlagActive) {
    return [...columnsListA, ...copyColumnsListB];
  }
  return [
    ...columnsListA,
    {
      [Properties.Key]: ColumnsNames.ProcessingCurrency,
      [Properties.ColumnName]: ColumnsNames.ProcessingCurrency,
      [Properties.Title]: 'Processing Currency',
      [Properties.Width]: 160,
      [Properties.FlexGrow]: 1,
      [Properties.SortingEnabled]: true,
      [Properties.Tooltip]: `The processing currency as set in ${brandName}.`,
      [Properties.TooltipPlacement]: 'top',
      [Properties.GetCellValue]: (row) => {
        if (row.onboarding) {
          return null;
        }
        return `${row.providerCurrency} (${getSymbolFromCurrency(row.providerCurrency)})`;
      },
    },
    ...copyColumnsListB,
  ];
};

export const getCSVColumns = (isCurrencyFeatureFlagActive) =>
  buildColumnsConfig(isCurrencyFeatureFlagActive)
    .filter((column) => ![ColumnsNames.Onboarding, ColumnsNames.Delete].includes(column[Properties.Key]))
    .map((column) => {
      return pick(column, [Properties.Key, Properties.Title, Properties.GetCellValue]);
    });

export const getTableColumns = (isCurrencyFeatureFlagActive, isMSPWithAzureAccounts, brandName) =>
  buildColumnsConfig(isCurrencyFeatureFlagActive, isMSPWithAzureAccounts, brandName).map((column) => {
    return pick(column, [
      Properties.Key,
      Properties.FlexGrow,
      Properties.Title,
      Properties.GetCellValue,
      Properties.Tooltip,
      Properties.TooltipPlacement,
      Properties.CloudTypeIds,
    ]);
  });

export const getTableColumnsWidths = (isCurrencyFeatureFlagActive, isMSPWithAzureAccounts) =>
  buildColumnsConfig(isCurrencyFeatureFlagActive, isMSPWithAzureAccounts).map((column) => {
    return pick(column, [
      Properties.ColumnName,
      Properties.Width,
      Properties.FlexGrow,
      Properties.SortingEnabled,
      Properties.Compare,
      Properties.Tooltip,
    ]);
  });

export const getTableSorting = (isCurrencyFeatureFlagActive, isMSPWithAzureAccounts) =>
  buildColumnsConfig(isCurrencyFeatureFlagActive, isMSPWithAzureAccounts)
    .filter((column) =>
      [ColumnsNames.Onboarding, ColumnsNames.CloudTypeId, ColumnsNames.AccountId].includes(column[Properties.Key]),
    )
    .map((column) => {
      return pick(column, [Properties.ColumnName, Properties.Direction]);
    });
